import { getPlatform } from "../../platform";
/**
 * OffchainAuthProvider wraps a real auth provider (e.g., Phantom) for the poofnet environment.
 *
 * signMessage shows a custom confirmation modal and then delegates to the wrapped
 * provider for a real detached signature over the exact offchain transaction message.
 */
export class OffchainAuthProvider {
    constructor(wrappedProvider, config = {}) {
        this.wrappedProvider = wrappedProvider;
        this.config = config;
    }
    // ============ Delegated Methods ============
    async login() {
        const user = await this.wrappedProvider.login();
        if (user) {
            return Object.assign(Object.assign({}, user), { provider: this });
        }
        return null;
    }
    // Forward per-call login overrides (theme/title/subtitle and the `method`
    // directive) to the wrapped provider. Without this, the top-level login()
    // forwarder (which duck-types `setLoginOverrides` on the active provider)
    // skips the wrapper and the override — including `method` — is dropped on
    // offchain/Poofnet builds.
    setLoginOverrides(opts) {
        var _a, _b;
        (_b = (_a = this.wrappedProvider).setLoginOverrides) === null || _b === void 0 ? void 0 : _b.call(_a, opts);
    }
    async logout() {
        await this.wrappedProvider.logout();
    }
    async restoreSession() {
        const user = await this.wrappedProvider.restoreSession();
        if (user) {
            return Object.assign(Object.assign({}, user), { provider: this });
        }
        return null;
    }
    async signTransaction(tx) {
        if (getPlatform().hasDOM) {
            await this.showUnsupportedTransactionModal();
        }
        throw new Error('Poofnet does not support real Solana transactions. Deploy your project to mainnet to use this feature.');
    }
    /**
     * Sign and submit transaction - not supported in poofnet environment.
     * See the real providers (PhantomWalletProvider, SolanaKeypairProvider)
     * for the full implementation with blockhash handling and feePayer support.
     */
    async signAndSubmitTransaction(_transaction, _feePayer) {
        if (getPlatform().hasDOM) {
            await this.showUnsupportedTransactionModal();
        }
        throw new Error('Poofnet does not support real Solana transactions. Deploy your project to mainnet to use this feature.');
    }
    async getNativeMethods() {
        return this.wrappedProvider.getNativeMethods();
    }
    // Forward wallet export to the wrapped provider (only Privy embedded wallets
    // implement it). Duck-typed so wrappers around non-embedded providers throw
    // a clear error rather than a "not a function" crash.
    async exportWallet(address) {
        if (typeof this.wrappedProvider.exportWallet !== 'function') {
            throw new Error('Wallet export is not available for this wallet.');
        }
        await this.wrappedProvider.exportWallet(address);
    }
    // ============ signMessage delegates to wrapped provider ============
    async signMessage(message) {
        var _a, _b, _c, _d, _e, _f;
        (_b = (_a = this.config).onSigningStart) === null || _b === void 0 ? void 0 : _b.call(_a);
        try {
            // On RN (no DOM), auto-confirm — there's no modal to show.
            // On web, show the confirmation modal.
            if (getPlatform().hasDOM) {
                const confirmed = await this.showTransactionModal(message);
                if (!confirmed) {
                    throw new Error("Transaction rejected by user");
                }
            }
            const signature = await this.wrappedProvider.signMessage(message);
            // Callback: signing complete
            (_d = (_c = this.config).onSigningComplete) === null || _d === void 0 ? void 0 : _d.call(_c, signature);
            return signature;
        }
        catch (error) {
            (_f = (_e = this.config).onSigningError) === null || _f === void 0 ? void 0 : _f.call(_e, error);
            throw error;
        }
    }
    // ============ Modal Implementation ============
    async showUnsupportedTransactionModal() {
        return new Promise((resolve) => {
            // Create modal container. Captured in this closure so every teardown
            // path acts on exactly this prompt, never on a newer one that may have
            // replaced a shared field.
            const container = document.createElement("div");
            container.id = "poofnet-unsupported-modal";
            container.appendChild(this.buildUnsupportedModalContent());
            document.body.appendChild(container);
            // Add styles
            const style = document.createElement("style");
            style.textContent = this.getModalStyles();
            container.appendChild(style);
            // Mark all OTHER body children as inert, recording only the ones this
            // modal changed (skipping any already-inert) so cleanup restores exactly
            // what we set and never re-enables app-owned inert regions.
            const selfInerted = [];
            document.body.childNodes.forEach((child) => {
                if (child !== container &&
                    child instanceof HTMLElement &&
                    !child.hasAttribute("inert")) {
                    child.setAttribute("inert", "");
                    selfInerted.push(child);
                }
            });
            // Animate in
            requestAnimationFrame(() => {
                const overlay = container.querySelector(".poofnet-modal-overlay");
                const content = container.querySelector(".poofnet-modal-content");
                if (overlay)
                    overlay.style.opacity = "1";
                if (content) {
                    content.style.opacity = "1";
                    content.style.transform = "translateY(0)";
                }
            });
            // Single idempotent teardown for every settlement path: removes the key
            // handler, restores only self-added inert, and removes exactly this
            // captured container after the exit animation.
            let settled = false;
            const escHandler = (e) => {
                if (e.key === "Escape")
                    dismiss();
            };
            const cleanup = () => {
                if (settled)
                    return;
                settled = true;
                document.removeEventListener("keydown", escHandler);
                for (const el of selfInerted)
                    el.removeAttribute("inert");
                const overlay = container.querySelector(".poofnet-modal-overlay");
                const content = container.querySelector(".poofnet-modal-content");
                if (overlay)
                    overlay.style.opacity = "0";
                if (content) {
                    content.style.opacity = "0";
                    content.style.transform = "translateY(20px)";
                }
                setTimeout(() => container.remove(), 200);
            };
            const dismiss = () => {
                cleanup();
                resolve();
            };
            // Handle close button
            const closeBtn = container.querySelector("#poofnet-close-btn");
            closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.addEventListener("click", dismiss);
            // Handle cancel button
            const cancelBtn = container.querySelector("#poofnet-cancel-btn");
            cancelBtn === null || cancelBtn === void 0 ? void 0 : cancelBtn.addEventListener("click", dismiss);
            // Handle overlay click
            const overlay = container.querySelector(".poofnet-modal-overlay");
            overlay === null || overlay === void 0 ? void 0 : overlay.addEventListener("click", (e) => {
                if (e.target === overlay)
                    dismiss();
            });
            // Handle escape key
            document.addEventListener("keydown", escHandler);
        });
    }
    async showTransactionModal(message) {
        return new Promise((resolve) => {
            // Parse the transaction message for display
            let parsed = {};
            try {
                parsed = JSON.parse(message);
            }
            catch (_a) {
                // If not JSON, just show raw message
            }
            // Create modal container. Captured in this closure so every teardown
            // path acts on exactly this prompt, never on a newer one that may have
            // replaced a shared field.
            const container = document.createElement("div");
            container.id = "poofnet-tx-modal";
            container.appendChild(this.buildTransactionModalContent(parsed, message));
            document.body.appendChild(container);
            // Add styles
            const style = document.createElement("style");
            style.textContent = this.getModalStyles();
            container.appendChild(style);
            // Mark all OTHER body children as inert to disable focus traps from
            // other modals, recording only the ones this modal changed (skipping
            // any already-inert) so cleanup restores exactly what we set and never
            // re-enables app-owned inert regions.
            const selfInerted = [];
            document.body.childNodes.forEach((child) => {
                if (child !== container &&
                    child instanceof HTMLElement &&
                    !child.hasAttribute("inert")) {
                    child.setAttribute("inert", "");
                    selfInerted.push(child);
                }
            });
            // Animate in
            requestAnimationFrame(() => {
                const overlay = container.querySelector(".poofnet-modal-overlay");
                const content = container.querySelector(".poofnet-modal-content");
                if (overlay)
                    overlay.style.opacity = "1";
                if (content) {
                    content.style.opacity = "1";
                    content.style.transform = "translateY(0)";
                }
            });
            // Single idempotent teardown for every settlement path: removes the key
            // handler, restores only self-added inert, and removes exactly this
            // captured container after the exit animation.
            let settled = false;
            const escHandler = (e) => {
                if (e.key === "Escape")
                    settle(false);
            };
            const cleanup = () => {
                if (settled)
                    return;
                settled = true;
                document.removeEventListener("keydown", escHandler);
                for (const el of selfInerted)
                    el.removeAttribute("inert");
                const overlay = container.querySelector(".poofnet-modal-overlay");
                const content = container.querySelector(".poofnet-modal-content");
                if (overlay)
                    overlay.style.opacity = "0";
                if (content) {
                    content.style.opacity = "0";
                    content.style.transform = "translateY(20px)";
                }
                setTimeout(() => container.remove(), 200);
            };
            const settle = (confirmed) => {
                cleanup();
                resolve(confirmed);
            };
            // Handle confirm
            const confirmBtn = container.querySelector("#poofnet-confirm-btn");
            confirmBtn === null || confirmBtn === void 0 ? void 0 : confirmBtn.addEventListener("click", () => settle(true));
            // Handle cancel
            const cancelBtn = container.querySelector("#poofnet-cancel-btn");
            cancelBtn === null || cancelBtn === void 0 ? void 0 : cancelBtn.addEventListener("click", () => settle(false));
            // Handle close button
            const closeBtn = container.querySelector("#poofnet-close-btn");
            closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.addEventListener("click", () => settle(false));
            // Handle overlay click
            const overlay = container.querySelector(".poofnet-modal-overlay");
            overlay === null || overlay === void 0 ? void 0 : overlay.addEventListener("click", (e) => {
                if (e.target === overlay)
                    settle(false);
            });
            // Handle escape key
            document.addEventListener("keydown", escHandler);
        });
    }
    createElement(tag, className, text) {
        const el = document.createElement(tag);
        if (className)
            el.className = className;
        if (text !== undefined)
            el.textContent = text;
        return el;
    }
    appendTextElement(parent, tag, className, text) {
        const el = this.createElement(tag, className, text);
        parent.appendChild(el);
        return el;
    }
    buildModalShell(icon, title) {
        const overlay = this.createElement("div", "poofnet-modal-overlay");
        const content = this.createElement("div", "poofnet-modal-content");
        overlay.appendChild(content);
        const close = this.createElement("button", "poofnet-close-btn", "×");
        close.id = "poofnet-close-btn";
        close.setAttribute("aria-label", "Close");
        close.type = "button";
        content.appendChild(close);
        const header = this.createElement("div", "poofnet-modal-header");
        this.appendTextElement(header, "div", "poofnet-modal-icon", icon);
        this.appendTextElement(header, "h2", "poofnet-modal-title", title);
        this.appendTextElement(header, "p", "poofnet-modal-subtitle", "Poofnet Simulated Blockchain");
        content.appendChild(header);
        const body = this.createElement("div", "poofnet-modal-body");
        content.appendChild(body);
        const footer = this.createElement("div", "poofnet-modal-footer");
        content.appendChild(footer);
        return { overlay, body, footer };
    }
    buildInfoBox(text, warning = false) {
        const info = this.createElement("div", "poofnet-info-box");
        if (warning)
            info.style.background = "#fef3c7";
        this.appendTextElement(info, "span", "poofnet-info-icon", "ℹ️");
        const infoText = this.createElement("span", "poofnet-info-text", text);
        if (warning)
            infoText.style.color = "#92400e";
        info.appendChild(infoText);
        return info;
    }
    buildUnsupportedModalContent() {
        const { overlay, body, footer } = this.buildModalShell("⚠️", "Transaction Not Supported");
        body.appendChild(this.buildInfoBox("Poofnet is a simulated blockchain and does not support real Solana transactions. Deploy your project to mainnet to test real transactions.", true));
        const close = this.createElement("button", "poofnet-btn poofnet-btn-cancel", "Close");
        close.id = "poofnet-cancel-btn";
        close.type = "button";
        close.style.flex = "1";
        footer.appendChild(close);
        return overlay;
    }
    formatPath(path) {
        const parts = path.split("/").filter(Boolean);
        if (parts.length >= 2) {
            return {
                collection: parts[0],
                documentId: parts[parts.length - 1],
            };
        }
        return { collection: parts[0] || path, documentId: null };
    }
    formatFieldValue(value) {
        if (value === null || value === undefined)
            return "null";
        if (typeof value === "string") {
            return value.length > 30
                ? `"${value.slice(0, 30)}..."`
                : `"${value}"`;
        }
        if (typeof value === "number" || typeof value === "boolean")
            return String(value);
        if (Array.isArray(value))
            return `[${value.length} items]`;
        if (typeof value === "object")
            return `{${Object.keys(value).length} fields}`;
        return String(value);
    }
    getActionDescription(type, collection, isNew) {
        if (type === "delete")
            return `Delete from ${collection}`;
        return isNew ? `Create in ${collection}` : `Update ${collection}`;
    }
    buildTransactionModalContent(parsed, rawMessage) {
        const instructions = parsed.instructions || [];
        // Group instructions by type for summary
        const sets = instructions.filter((i) => i.type === "set");
        const deletes = instructions.filter((i) => i.type === "delete");
        // Create summary text
        let summaryText = "";
        if (sets.length > 0 && deletes.length > 0) {
            summaryText = `${sets.length} update${sets.length > 1 ? "s" : ""}, ${deletes.length} delete${deletes.length > 1 ? "s" : ""}`;
        }
        else if (sets.length > 0) {
            summaryText = `${sets.length} update${sets.length > 1 ? "s" : ""}`;
        }
        else if (deletes.length > 0) {
            summaryText = `${deletes.length} delete${deletes.length > 1 ? "s" : ""}`;
        }
        const { overlay, body, footer } = this.buildModalShell("🔐", "Confirm Transaction");
        if (instructions.length > 0) {
            if (summaryText) {
                this.appendTextElement(body, "div", "poofnet-summary", summaryText);
            }
            const list = this.createElement("div", "poofnet-instructions-list");
            for (const inst of instructions) {
                list.appendChild(this.buildInstructionElement(inst));
            }
            body.appendChild(list);
        }
        else {
            const section = this.createElement("div", "poofnet-section");
            this.appendTextElement(section, "h3", "poofnet-section-title", "Message");
            const raw = this.createElement("pre", "poofnet-raw-message", rawMessage.slice(0, 200) + (rawMessage.length > 200 ? "..." : ""));
            section.appendChild(raw);
            body.appendChild(section);
        }
        body.appendChild(this.buildInfoBox("This is a simulated transaction. No real fees will be charged."));
        const cancel = this.createElement("button", "poofnet-btn poofnet-btn-cancel", "Cancel");
        cancel.id = "poofnet-cancel-btn";
        cancel.type = "button";
        footer.appendChild(cancel);
        const confirm = this.createElement("button", "poofnet-btn poofnet-btn-confirm", "Confirm");
        confirm.id = "poofnet-confirm-btn";
        confirm.type = "button";
        footer.appendChild(confirm);
        return overlay;
    }
    buildInstructionElement(inst) {
        const { collection, documentId } = this.formatPath(inst.path);
        const isDelete = inst.type === "delete";
        const card = this.createElement("div", `poofnet-instruction ${isDelete ? "poofnet-action-delete" : "poofnet-action-set"}`);
        const header = this.createElement("div", "poofnet-instruction-header");
        this.appendTextElement(header, "span", "poofnet-instruction-icon", isDelete ? "🗑️" : "✏️");
        this.appendTextElement(header, "span", "poofnet-instruction-action", isDelete ? "Delete" : "Save");
        this.appendTextElement(header, "span", "poofnet-instruction-collection", collection);
        card.appendChild(header);
        if (documentId) {
            this.appendTextElement(card, "div", "poofnet-instruction-id", `ID: ${documentId}`);
        }
        if (inst.data && !isDelete) {
            const fieldKeys = Object.keys(inst.data).filter((key) => !key.startsWith("_"));
            const visibleFields = fieldKeys.slice(0, 4);
            if (visibleFields.length > 0) {
                const fields = this.createElement("div", "poofnet-fields");
                for (const key of visibleFields) {
                    const row = this.createElement("div", "poofnet-field");
                    this.appendTextElement(row, "span", "poofnet-field-key", `${key}:`);
                    this.appendTextElement(row, "span", "poofnet-field-value", this.formatFieldValue(inst.data[key]));
                    fields.appendChild(row);
                }
                if (fieldKeys.length > 4) {
                    this.appendTextElement(fields, "div", "poofnet-field-more", `+${fieldKeys.length - 4} more fields`);
                }
                card.appendChild(fields);
            }
        }
        return card;
    }
    getModalStyles() {
        return `
      /* Ensure inert elements remain visible while being non-interactive */
      [inert] {
        visibility: visible !important;
        opacity: 1 !important;
        display: unset !important;
      }

      .poofnet-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        opacity: 0;
        transition: opacity 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .poofnet-modal-content {
        background: white;
        border-radius: 16px;
        width: 90%;
        max-width: 420px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.2s ease;
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
      }

      .poofnet-close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 28px;
        height: 28px;
        border: none;
        background: #f3f4f6;
        border-radius: 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6b7280;
        transition: all 0.15s ease;
        z-index: 1;
      }

      .poofnet-close-btn:hover {
        background: #e5e7eb;
        color: #374151;
      }

      .poofnet-modal-header {
        padding: 24px 24px 16px;
        text-align: center;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;
      }

      .poofnet-modal-icon {
        font-size: 40px;
        margin-bottom: 8px;
      }

      .poofnet-modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .poofnet-modal-subtitle {
        margin: 4px 0 0;
        font-size: 13px;
        color: #8b5cf6;
        font-weight: 500;
      }

      .poofnet-modal-body {
        padding: 16px 20px;
        overflow-y: auto;
        flex: 1;
      }

      .poofnet-summary {
        font-size: 13px;
        color: #6b7280;
        margin-bottom: 12px;
        text-align: center;
      }

      .poofnet-section {
        margin-bottom: 16px;
      }

      .poofnet-section-title {
        font-size: 12px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin: 0 0 8px;
      }

      .poofnet-instructions-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .poofnet-instruction {
        background: #f9fafb;
        border-radius: 10px;
        padding: 12px;
        border-left: 3px solid #8b5cf6;
      }

      .poofnet-instruction.poofnet-action-delete {
        border-left-color: #ef4444;
      }

      .poofnet-instruction-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
      }

      .poofnet-instruction-icon {
        font-size: 16px;
        flex-shrink: 0;
      }

      .poofnet-instruction-action {
        font-size: 13px;
        font-weight: 600;
        color: #8b5cf6;
      }

      .poofnet-action-delete .poofnet-instruction-action {
        color: #ef4444;
      }

      .poofnet-instruction-collection {
        font-size: 13px;
        font-weight: 500;
        color: #374151;
      }

      .poofnet-instruction-id {
        font-size: 11px;
        color: #9ca3af;
        font-family: monospace;
        margin-bottom: 8px;
        word-break: break-all;
      }

      .poofnet-fields {
        background: white;
        border-radius: 6px;
        padding: 8px 10px;
        margin-top: 8px;
      }

      .poofnet-field {
        display: flex;
        gap: 8px;
        padding: 4px 0;
        font-size: 12px;
        border-bottom: 1px solid #f3f4f6;
      }

      .poofnet-field:last-child {
        border-bottom: none;
      }

      .poofnet-field-key {
        color: #6b7280;
        font-weight: 500;
        flex-shrink: 0;
      }

      .poofnet-field-value {
        color: #374151;
        word-break: break-all;
      }

      .poofnet-field-more {
        font-size: 11px;
        color: #9ca3af;
        padding-top: 4px;
        font-style: italic;
      }

      .poofnet-raw-message {
        background: #f9fafb;
        border-radius: 8px;
        padding: 12px;
        font-size: 11px;
        font-family: monospace;
        color: #4b5563;
        white-space: pre-wrap;
        word-break: break-all;
        margin: 0;
        max-height: 120px;
        overflow-y: auto;
      }

      .poofnet-info-box {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #faf5ff;
        border-radius: 8px;
        padding: 10px 12px;
        margin-top: 12px;
      }

      .poofnet-info-icon {
        font-size: 14px;
        flex-shrink: 0;
      }

      .poofnet-info-text {
        font-size: 11px;
        color: #7c3aed;
        line-height: 1.4;
      }

      .poofnet-modal-footer {
        padding: 16px 20px 20px;
        display: flex;
        gap: 12px;
        flex-shrink: 0;
        border-top: 1px solid #f0f0f0;
      }

      .poofnet-btn {
        flex: 1;
        padding: 12px 16px;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
        border: none;
      }

      .poofnet-btn-cancel {
        background: #f3f4f6;
        color: #4b5563;
      }

      .poofnet-btn-cancel:hover {
        background: #e5e7eb;
      }

      .poofnet-btn-confirm {
        background: #8b5cf6;
        color: white;
      }

      .poofnet-btn-confirm:hover {
        background: #7c3aed;
      }
    `;
    }
}
